import { useConfigStore } from "./config";
import { defineStore } from "pinia";
import {
  ApiClient,
  SearchClient,
  KpiV3Client,
  EventsClient,
  DependenciesClient,
  KnowledgeClient,
  PushNotificationsClient,
} from "@/services/clients";
import { AuthClient } from "@/services/auth";
import { SUPPORT_LOCALES } from "@/i18n";
import { StatusBar, Style } from "@capacitor/status-bar";
import mixpanelTracker from "@/utils/mixpanel-tracker";
import { registerNotifications } from "@/utils/notifications";
import {
  AuthType,
  Client,
  FabriqState,
  RoutineExecutionView,
  Token,
  User,
} from "@/types";
import { UserService } from "@/services/user";
import { Capacitor } from "@capacitor/core";
import storage from "@/utils/storage";
import { fromUnixTime } from "date-fns";
import buildSocket from "@/utils/socket";
import { Badge } from "@capawesome/capacitor-badge";
import loader from "@/utils/loader";
import { version } from "../../package.json";
import { useClientStore } from "./client";
import config from "@/config";
import { MessagingSocket } from "@/utils/socket-builder";
import { buildSentryManager, type SentryManager } from "@/utils/sentry";
import { App } from "vue";
import { localLogger } from "@/utils/localLogger";
import Intercom, * as IntercomHelpers from "@intercom/messenger-js-sdk";

export const FUTURE_SUBDOMAIN_KEY = "future-subdomain";
export const FUTURE_URL_KEY = "future-url";

let socket: MessagingSocket | null = null;
let sentryManager: SentryManager | null = null;

export type FabriqStore = ReturnType<typeof useFabriqStore>;
export const useFabriqStore = defineStore("core", {
  state: (): FabriqState => {
    return {
      version,
      today: new Date(),
      locked: false,
      connected: false,
      online: true,
      hideTabs: false,
      token: null,
      loading: false,
      requests: 0,
      locale: "en",
      executionView: RoutineExecutionView.List,
      user: null,
      configChanged: false,
      initializationState: { type: "pending" },
      platform: Capacitor.getPlatform(),
      page: "tickets",
      previousPage: "tickets",
      colorScheme: "dark",
      url: null,
      theme: undefined,
      showClosedTasks: [],
      thresholdDuration: 2000,
      lastRequestDuration: 0,
      lastRequestDurations: [],
      pageToOpenAfterLogin: "",
      buildVersion: "",
      authConfig: null,
      intercomEnabled: config.intercomEnabled ?? false,
      intercomInitialized: false,
      intercomUnreadCount: 0,
    };
  },
  actions: {
    setUser(user: User) {
      this.user = user;
    },
    setLastRequestDuration(duration: number) {
      this.lastRequestDuration = duration;
      this.lastRequestDurations.unshift(duration);
      this.lastRequestDurations = this.lastRequestDurations.slice(0, 5);
    },
    resetRequestDuration() {
      this.lastRequestDurations = [];
    },
    setVersion(version: string) {
      this.version = version;
    },
    setBuildVersion(version: string) {
      this.buildVersion = version;
      storage.set("build-version", version);
    },
    async setBuildVersionFromStorage() {
      this.buildVersion = (await storage.get("build-version")) || "";
    },
    setLocale(locale: string) {
      if (!SUPPORT_LOCALES.includes(locale)) return;
      this.locale = locale;
      storage.set("fabriq-locale", locale);
    },
    setColorScheme(theme: string) {
      this.colorScheme = theme;
    },
    setPageToOpenAfterLogin(page: string) {
      this.pageToOpenAfterLogin = page;
    },
    async setTheme(theme: string | undefined) {
      this.theme = theme;
      if (this.theme) {
        localStorage.setItem("fabriq-theme", this.theme);
      } else {
        localStorage.removeItem("fabriq-theme");
      }
      document.body.classList.remove("dark");
      await StatusBar.setStyle({ style: Style.Light }).catch((e) =>
        console.warn(e)
      );
      if (
        this.theme === "dark" ||
        (!this.theme && this.colorScheme === "dark")
      ) {
        document.body.classList.add("dark");
        await StatusBar.setStyle({ style: Style.Dark }).catch((e) =>
          console.warn(e)
        );
      }
    },
    setLoading(loading: boolean) {
      this.loading = loading;
    },
    setPage(page: string) {
      if (this.page === page) return;
      mixpanelTracker.track(`open | ${page}`, { from: this.page });
      localLogger.log(`GOTO Page ${page} from ${this.page}`);
      this.previousPage = this.page;
      this.page = page;
    },
    setOnline(online: boolean) {
      this.online = online;
      if (!online) loader.hide();
      ApiClient.setOnline(this.online);
      SearchClient.setOnline(this.online);
      KpiV3Client.setOnline(this.online);
      EventsClient.setOnline(this.online);
      KnowledgeClient.setOnline(this.online);
      DependenciesClient.setOnline(this.online);
      PushNotificationsClient.setOnline(this.online);
    },
    closeTask(uuid: string) {
      this.showClosedTasks.push(uuid);
      setTimeout(() => {
        const idx = this.showClosedTasks.indexOf(uuid);
        if (idx < 0) return;
        this.showClosedTasks = [
          ...this.showClosedTasks.slice(0, idx),
          ...this.showClosedTasks.slice(idx + 1),
        ];
      }, 5000);
    },
    cancelCloseTask(uuid: string) {
      const idx = this.showClosedTasks.indexOf(uuid);
      if (idx < 0) return;
      this.showClosedTasks = [
        ...this.showClosedTasks.slice(0, idx),
        ...this.showClosedTasks.slice(idx + 1),
      ];
    },
    setLocked(locked: boolean) {
      this.locked = locked;
    },
    setInitializationState(state: FabriqState["initializationState"]) {
      this.initializationState = state;
    },
    startRequest() {
      this.requests += 1;
    },
    endRequest() {
      if (this.requests > 0) this.requests -= 1;
    },
    setToken(token: Token | null) {
      this.token = token;
      this.connected = !!token;
      ApiClient.setToken(token);
      SearchClient.setToken(token);
      KpiV3Client.setToken(token);
      EventsClient.setToken(token);
      KnowledgeClient.setToken(token);
      DependenciesClient.setToken(token);
      PushNotificationsClient.setToken(token);
      ApiClient.checkToken(this);
      SearchClient.checkToken(this);
      KpiV3Client.checkToken(this);
      EventsClient.checkToken(this);
      KnowledgeClient.checkToken(this);
      DependenciesClient.checkToken(this);
      PushNotificationsClient.checkToken(this);
    },
    async setCredentials(email: string, password: string) {
      AuthClient.setCredentials(email, password);
      await this.initializeApiClients();
    },
    async checkCurrent(): Promise<{ token: Token; user: any } | null> {
      this.loading = true;
      const response = await AuthClient.checkCurrentSession();
      if (!response) {
        this.loading = false;
        return null;
      }
      const { token, user } = response;

      this.loading = false;
      if (!token || !user) return null;
      return {
        token: {
          type: AuthType.Future,
          accessToken: token.access.token,
          refreshToken: token.refresh.token,
          expiresAt: fromUnixTime(token.access.payload.exp),
        },
        user,
      };
    },

    async clearUrl() {
      this.url = null;
      await storage.set(FUTURE_URL_KEY, null);
      localStorage.removeItem("savedUrl");

      // #TODO: remove this condition in September 2025
      if (localStorage.getItem("subdomainUrl") !== null) {
        await storage.set(FUTURE_SUBDOMAIN_KEY, null);
        localStorage.removeItem("subdomainUrl");
      }
    },

    async initializeApiClients(url?: string) {
      await localLogger.log(`initializeApiClients ${url}`);
      if (url) {
        await storage.set(FUTURE_URL_KEY, url);
      }

      let futureUrl =
        (await storage.get(FUTURE_URL_KEY)) ||
        (await storage.get(FUTURE_SUBDOMAIN_KEY)); // #TODO: remove the FUTURE_SUBDOMAIN_KEY part in September 2025
      await localLogger.log(
        `initializeApiClients storage futureUrl ${futureUrl}`
      );
      if (!futureUrl) {
        const savedUrl = localStorage.getItem("savedUrl");
        await localLogger.log(
          `initializeApiClients localStorage futureUrl ${savedUrl}`
        );
        if (savedUrl) {
          futureUrl = savedUrl;
          await storage.set(FUTURE_URL_KEY, savedUrl);
          localStorage.removeItem("savedUrl");
          localStorage.removeItem("subdomainUrl"); // #TODO: remove this condition in September 2025
        }
      }

      if (!sentryManager) {
        throw new Error("Sentry not setup; cannot report error");
      }
      this.url = futureUrl;
      sentryManager.setupSentry(futureUrl);
      ApiClient.applyServiceUrlToBaseUrl(futureUrl, config.futureLegacyApiPath);
      SearchClient.applyServiceUrlToBaseUrl(futureUrl, config.searchPrefix);
      KpiV3Client.applyServiceUrlToBaseUrl(futureUrl, config.futureKpiPrefix);
      EventsClient.applyServiceUrlToBaseUrl(
        futureUrl,
        config.futureEventsPrefix
      );
      KnowledgeClient.applyServiceUrlToBaseUrl(
        futureUrl,
        config.knowledgePrefix
      );
      DependenciesClient.applyServiceUrlToBaseUrl(
        futureUrl,
        config.futureDependenciesPrefix
      );
      PushNotificationsClient.applyServiceUrlToBaseUrl(
        futureUrl,
        config.pushNotificationsPath
      );
      AuthClient.applySubDomainUrl(futureUrl);
      this.authConfig =
        (await AuthClient.organizationAuthenticationConfig()) ?? null;
    },
    async login(email: string, password: string) {
      this.loading = true;
      const token = await AuthClient.login(email, password);
      this.setToken(token);
      this.loading = false;
      IntercomHelpers.shutdown();
    },
    async loadUserData(
      teamsStore?: any,
      init = false
    ): Promise<{ user: User; client?: Client } | undefined> {
      try {
        // eslint-disable-next-line fabriq-webapp/vue-composable-rules -- we're in a store
        const configStore = useConfigStore();
        // eslint-disable-next-line fabriq-webapp/vue-composable-rules -- we're in a store
        const clientStore = useClientStore();

        const user = await UserService.me();
        if (!user) {
          throw new Error("User not found");
        }
        localStorage.setItem("nb-login", "0");
        this.user = user;
        configStore.setConfig(user.config || {});

        mixpanelTracker.setUser(user);
        if (user.config?.hasPushNotifications === undefined && this.user?.id) {
          configStore.setHasNotifications(true);
          configStore.saveUserConfig();
        }

        await this.initIntercom(user);

        storage.writeToStorage("user", user);

        if (!init || !clientStore.client) {
          if (teamsStore) {
            await teamsStore.all({ id: user.id, flush: true });
            const teamSharingSettingsLoader = async (ev: any) => {
              // wait for teams to be loaded before loading team sharing settings
              const data = ev.data;
              if (data.name !== "teams") return;
              await teamsStore.loadTeamSharingSettings();
              storage.removeMessageListener(teamSharingSettingsLoader);
            };
            storage.addMessageListener(teamSharingSettingsLoader);
          }
          const client = await clientStore.load();
          storage.writeToStorage("client", client);
          await registerNotifications();
          await UserService.sendUserMobileData();
          return { user, client };
        }
        await registerNotifications();
        await UserService.sendUserMobileData();
        return { user };
      } catch (e) {
        const message = e instanceof Error ? e.message : e;
        await localLogger.log(`loadUserData error ${message}`);
        console.error(e);
      }
    },
    async loadUserOnly(user: User): Promise<User | undefined> {
      try {
        // eslint-disable-next-line fabriq-webapp/vue-composable-rules -- we're in a store
        const configStore = useConfigStore();
        this.user = user;
        configStore.setConfig(user.config || {});
        if (SUPPORT_LOCALES.includes(user.language)) {
          this.setLocale(user.language);
        }
        if (this.locale !== user.language) this.setLocale(user.language);
        mixpanelTracker.setUser(user);
        if (user.config?.hasPushNotifications === undefined && this.user?.id) {
          configStore.setHasNotifications(true);
          configStore.saveUserConfig();
        }
        storage.writeToStorage("user", user);
        return Promise.resolve(user);
      } catch (e) {
        console.error(e);
      }
    },
    async connectSocket() {
      if (!this.url) return;
      socket = await buildSocket(`${this.url}${config.messagingBaseUrl}`);
      socket.open();
    },
    buildSentry(app: App) {
      sentryManager = buildSentryManager(app, this.platform);
      AuthClient.setReportSentry(this.reportSentry);
    },
    reportSentry(error: Error, data?: Record<string, any>) {
      if (!sentryManager) {
        throw new Error("Sentry not setup; cannot report error");
      }
      try {
        sentryManager.reportError(error, data);
      } catch (e) {
        console.warn("Report error to Sentry failed", e);
      }
    },
    async setTeams(teamIds: Array<number>) {
      if (!socket) return;
      const token = await ApiClient.getFreshToken();
      socket.emit("teamSubscribe", {
        teams: teamIds,
        token: token?.accessToken,
      });
    },
    async clearPushNotifications() {
      const notificationsToken = await storage.get("push-token");
      if (notificationsToken) {
        try {
          await UserService.removePushNotificationToken(notificationsToken);
        } catch (e) {
          console.error(e);
        }
      }
      await storage.set("push-token", null);
    },
    async logout() {
      try {
        await AuthClient.logout(this.token);
      } catch (e) {
        this.reportSentry(e as Error);
        console.error(e);
      }
      const futureUrl = await storage.get(FUTURE_URL_KEY);
      await storage.clear();
      await storage.set(FUTURE_URL_KEY, futureUrl);
      await Badge.clear();
      this.user = null;
      this.token = null;
      // eslint-disable-next-line fabriq-webapp/vue-composable-rules -- we're in a store
      const clientStore = useClientStore();
      await clientStore.logout();
      this.connected = false;
      window.location.href = "/login";
      IntercomHelpers.shutdown();
      this.intercomInitialized = false;
    },
    async initIntercom(user?: User | null) {
      if (!this.intercomEnabled) return;

      try {
        if (!this.intercomInitialized) {
          Intercom({
            app_id: config.intercomAppId,
            hide_default_launcher: true,
            hide_notifications: true,
            utm_medium: "mobile",
          });

          IntercomHelpers.hideNotifications(true);
          // setup of intercom id is done at the build level
          IntercomHelpers.onUnreadCountChange((count: number) => {
            this.intercomUnreadCount = count;
          });
          this.intercomInitialized = true;
        }
        if (!user) return;

        // This allows us to track a potential switch between an anonymous user and a logged-in user.
        IntercomHelpers.trackEvent("intercom_init", { email: user.email });

        IntercomHelpers.update({
          user_id: user.future_user_id,
          email: user.email,
          name: `${user.first_name} ${user.last_name}`,
          user_hash: user.intercom_user_hash,
          company: {
            id: user.future_organization_id,
            name: this.url, // this is suboptimal
            subdomain: this.url,
          },
          utm_medium: "mobile",
        });
      } catch (e) {
        const message = e instanceof Error ? e.message : e;
        await localLogger.log(`Error initializing Intercom ${message}`);
      }
    },
    async openIntercomChat() {
      if (!this.intercomEnabled) return;
      try {
        await this.initIntercom(this.user);
        const logs = (await localLogger.getLogs()).split("\n");
        if (logs) {
          // Intercom has a limit of 65535 characters for event metadata
          // We will send logs in chunks of 60000 characters to avoid exceeding the limit

          let logsToSend = "";
          for (let i = 0; i < logs.length; i++) {
            const line = logs[i];
            if (line.length === 0) continue; // skip empty lines
            if (logsToSend.length + line.length < 60000) {
              logsToSend += line + "\n";
            } else {
              IntercomHelpers.trackEvent("fabriq_log", { logs: logsToSend });
              logsToSend = line + "\n";
            }
          }
          if (logsToSend) {
            IntercomHelpers.trackEvent("fabriq_log", { logs: logsToSend });
          }
        }
        IntercomHelpers.showMessages();
      } catch (e) {
        const message = e instanceof Error ? e.message : e;
        await localLogger.log(`Error opening Intercom chat ${message}`);
      }
    },
  },
});
